import React from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { Button } from 'primereact/button'

import ChargePointDetailsAdminInputs from '../../../common/features/chargepoint-details-widget/chargepoint-details-admin-inputs'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { ChargePoint, useChargePointUpdateChargerInformationMutation } from '../../../common/redux/api/web-api'
import { mapFormValuesToChargerInformation } from '../../../common/utils/objectMappers'
import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import useText from '../../../common/hooks/useText'
import Text from '../../../ui/Text'
import { WidgetLayout } from '../../../lib/widget-layout'
import ChargePointDetailsPartnerInputs from '../../../common/features/chargepoint-details-widget/chargepoint-details-partner-inputs'
import { SButtonsGroupContainer } from '../../../common/styles/Widget'
import {
    IChargerInformationFormInput,
    useChargerInfoValidation,
} from '../../../common/hooks/useChargePointInfoValidation'

import ChargePointDetailsSupportCapiInputs from './charge-point-capi-support-inputs'

export type ChargePointCapiDetailsWidgetProps = {
    chargePoint: ChargePoint
    loading: boolean
    className?: ClassName
    style?: React.CSSProperties
}

export function ChargePointCapiDetailsWidget({
    chargePoint,
    loading,
    className,
    style,
}: ChargePointCapiDetailsWidgetProps): React.ReactElement {
    const text = useText()
    const [updateChargerInfo, { isLoading: chargerInfoUpdating }] = useChargePointUpdateChargerInformationMutation()

    const chargerInfoFormValidation = useChargerInfoValidation(chargePoint)
    const {
        handleSubmit,
        reset,
        formState: { isDirty },
    } = chargerInfoFormValidation

    const saveChargePoint: SubmitHandler<IChargerInformationFormInput> = (formValues) => {
        updateChargerInfo({
            chargePointId: chargePoint.id,
            chargerInformation: mapFormValuesToChargerInformation(formValues),
        })
            .unwrap()
            .then(() => {
                notifySuccess(text('msgChargerUpdated'))
                reset(formValues, {
                    keepDirty: false,
                    keepDirtyValues: false,
                })
            })
            .catch(() => notifyError(text('msgChargePointNotUpdated')))
    }

    return (
        <WidgetLayout
            title={<Text code="lblChargerInformation" />}
            loadingStatus={loading}
            className={cn(className)}
            style={style}
            data-testid="details"
        >
            <FormProvider {...chargerInfoFormValidation}>
                <form onSubmit={handleSubmit(saveChargePoint)}>
                    {isAdminDomain && <ChargePointDetailsAdminInputs chargePoint={chargePoint} />}
                    {isPartnerDomain && <ChargePointDetailsPartnerInputs chargePoint={chargePoint} />}
                    {isSupportDomain && <ChargePointDetailsSupportCapiInputs chargePoint={chargePoint} />}
                    <SButtonsGroupContainer className="action-btns">
                        <Button
                            label={text('btnSave')}
                            type="submit"
                            disabled={loading || chargerInfoUpdating || !isDirty}
                            data-testid="save-btn"
                            icon="pi pi-save"
                        />
                    </SButtonsGroupContainer>
                </form>
            </FormProvider>
        </WidgetLayout>
    )
}
